import { toPng } from 'html-to-image'

/**
 * What the customer had in front of them when they agreed to something — a screenshot,
 * the page that produced it, and the browser it was rendered in. Corgi stores it against
 * the bind as the record of consent, so it is captured at the click, not rebuilt later.
 */

/** Django's default DATA_UPLOAD_MAX_MEMORY_SIZE is 2.5MB and a rejected POST fails checkout. */
const MAX_DOM_HTML_CHARS = 700_000
const MAX_STYLE_CHARS = 300_000
/** The capture is scaled into this box — a full quotes page is several thousand pixels tall. */
const MAX_SCREENSHOT_WIDTH = 1000
const MAX_SCREENSHOT_HEIGHT = 3000
const FALLBACK_LINE_HEIGHT = 22
const FALLBACK_PADDING = 16
/** 1x1 transparent PNG, standing in for an image the rasterizer is not allowed to read. */
const BLANK_IMAGE =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII='

export interface ClientContext {
  captured_at: string
  page_url: string
  referrer: string
  user_agent: string
  language: string
  languages: string[]
  platform: string
  timezone: string
  timezone_offset_minutes: number
  viewport_width: number
  viewport_height: number
  screen_width: number
  screen_height: number
  device_pixel_ratio: number
}

export interface DomEvidence {
  dom_html: string
  dom_screenshot_b64: string
  client_context: ClientContext
}

/** Site and path only: a query can carry a sign-in token and the customer's email. */
export function withoutQuery(url: string): string {
  if (!url) return ''
  try {
    const parsed = new URL(url)
    return `${parsed.origin}${parsed.pathname}`
  } catch {
    return ''
  }
}

/** Everything about the browser the customer used. The IP is stamped by the server. */
export function captureClientContext(): ClientContext {
  const now = new Date()
  return {
    captured_at: now.toISOString(),
    page_url: withoutQuery(window.location.href),
    referrer: withoutQuery(document.referrer),
    user_agent: navigator.userAgent,
    language: navigator.language,
    languages: [...navigator.languages],
    platform: navigator.platform,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    // Negated so it reads as a UTC offset rather than getTimezoneOffset's inverted sign.
    timezone_offset_minutes: -now.getTimezoneOffset(),
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight,
    screen_width: window.screen.width,
    screen_height: window.screen.height,
    device_pixel_ratio: window.devicePixelRatio,
  }
}

function escapeAttribute(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;')
}

/**
 * Every CSS rule currently in force, flattened into one block.
 *
 * Read off `document.styleSheets` rather than copied from the `<link>` and `<style>` tags
 * in head: in dev the styles are injected by script and in production they are a hashed
 * file, and only the live rules cover both. A snapshot without them is a wall of text.
 */
function pageStyles(): { css: string; hrefs: string[] } {
  const rules: string[] = []
  const hrefs: string[] = []
  for (const sheet of Array.from(document.styleSheets)) {
    try {
      for (const rule of Array.from(sheet.cssRules)) rules.push(rule.cssText)
    } catch {
      // A cross-origin sheet won't hand over its rules, so the snapshot links it instead.
      if (sheet.href) hrefs.push(sheet.href)
    }
  }
  const css = rules.join('\n')
  return { css: css.length > MAX_STYLE_CHARS ? css.slice(0, MAX_STYLE_CHARS) : css, hrefs }
}

/** The page as a document that opens and renders on its own, capped to what a POST can carry. */
function captureDomHtml(element: HTMLElement): string {
  const markup = (element === document.body ? element.outerHTML : `<body>${element.outerHTML}</body>`)
    // The code behind the page is not what the customer saw, and re-running it on a
    // snapshot would have it try to talk to the app.
    .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, '')
    .replace(/<script\b[^>]*\/>/gi, '')

  const { css, hrefs } = pageStyles()
  const head = [
    '<meta charset="utf-8">',
    `<base href="${escapeAttribute(withoutQuery(document.baseURI))}">`,
    ...hrefs.map((href) => `<link rel="stylesheet" href="${escapeAttribute(href)}">`),
    `<style>${css}</style>`,
  ].join('')

  const html = `<!doctype html><html><head>${head}</head>${markup}</html>`
  if (html.length <= MAX_DOM_HTML_CHARS) return html
  return `${html.slice(0, MAX_DOM_HTML_CHARS)}<!-- truncated at ${MAX_DOM_HTML_CHARS} characters -->`
}

/**
 * The element's rendered text on a plain canvas — the floor, for when rasterizing fails.
 * Unreadable for a whole page, but it still carries the words that were on screen.
 */
export function captureElementScreenshot(element: HTMLElement): string {
  const rect = element.getBoundingClientRect()
  const width = Math.round(rect.width) || 800
  const text = element.innerText || ''
  const lines = text.split('\n').filter((line) => line.trim())
  const height = Math.min(
    MAX_SCREENSHOT_HEIGHT,
    Math.max(80, lines.length * FALLBACK_LINE_HEIGHT + FALLBACK_PADDING * 2)
  )

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) return ''

  ctx.fillStyle = '#f9fafb'
  ctx.fillRect(0, 0, width, height)
  ctx.fillStyle = '#111827'
  ctx.font = '14px system-ui, -apple-system, sans-serif'
  ctx.textBaseline = 'top'

  let y = FALLBACK_PADDING
  for (const rawLine of lines) {
    if (y > height) break
    const words = rawLine.split(' ')
    let currentLine = ''
    for (const word of words) {
      const test = currentLine + (currentLine ? ' ' : '') + word
      if (ctx.measureText(test).width > width - FALLBACK_PADDING * 2 && currentLine) {
        ctx.fillText(currentLine, FALLBACK_PADDING, y)
        currentLine = word
        y += FALLBACK_LINE_HEIGHT
      } else {
        currentLine = test
      }
    }
    if (currentLine) {
      ctx.fillText(currentLine, FALLBACK_PADDING, y)
      y += FALLBACK_LINE_HEIGHT
    }
  }

  return canvas.toDataURL('image/png').split(',')[1] ?? ''
}

/**
 * A real picture of the element, scaled into the size budget.
 *
 * `skipFonts`: embedding the webfonts means inlining every woff2 into the SVG, which is
 * slow enough to be felt on the checkout button and fails outright if one won't fetch.
 * The text renders in the fallback face rather than not at all.
 *
 * `imagePlaceholder`: the carrier logos are served from static.cai.insure with no CORS
 * header, so reading their bytes throws and takes the whole capture down with it. One
 * blank logo is a far better record than no screenshot.
 */
export async function captureScreenshot(element: HTMLElement): Promise<string> {
  const rect = element.getBoundingClientRect()
  const width = Math.max(1, Math.round(rect.width))
  const height = Math.max(1, Math.round(rect.height))
  const scale = Math.min(1, MAX_SCREENSHOT_WIDTH / width, MAX_SCREENSHOT_HEIGHT / height)
  try {
    const dataUrl = await toPng(element, {
      width,
      height,
      canvasWidth: Math.round(width * scale),
      canvasHeight: Math.round(height * scale),
      pixelRatio: 1,
      backgroundColor: '#ffffff',
      skipFonts: true,
      imagePlaceholder: BLANK_IMAGE,
    })
    return dataUrl.split(',')[1] ?? ''
  } catch (error) {
    // Silent degradation to the text render is what made the first capture unreadable.
    console.warn('Checkout screenshot could not be rasterized', error)
    return captureElementScreenshot(element)
  }
}

/**
 * Evidence for `element`, or for the whole page when it is left out — the quotes page
 * puts its cart in a portal, so only the document holds both halves of the checkout.
 */
export async function captureDomEvidence(element?: HTMLElement | null): Promise<DomEvidence> {
  const target = element ?? (typeof document === 'undefined' ? null : document.body)
  if (!target) {
    return { dom_html: '', dom_screenshot_b64: '', client_context: captureClientContext() }
  }
  // Before the await, so the context timestamps the click rather than the render.
  const client_context = captureClientContext()
  return {
    dom_html: captureDomHtml(target),
    dom_screenshot_b64: await captureScreenshot(target),
    client_context,
  }
}
